import { useState, useEffect } from 'react'
import { supervisorAPI } from '../../services/api'
import AccordionSection from './AccordionSection'

/**
 * SupervisorAnalysis - Sección de análisis IA del supervisor de ventas
 * Muestra análisis existente y permite gatillar uno nuevo/actualizado
 */
export default function SupervisorAnalysis({ phone }) {
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzeSuccess, setAnalyzeSuccess] = useState(false)
  const [error, setError] = useState(null)

  // Cargar análisis existente
  useEffect(() => {
    if (!phone) return
    setLoading(true)
    setError(null)
    supervisorAPI.getAnalysis(phone)
      .then(res => {
        setAnalysis(res?.exists ? res.analysis : null)
      })
      .catch(err => {
        console.error('[SupervisorAnalysis] Error cargando:', err)
        setError('Error cargando análisis')
      })
      .finally(() => setLoading(false))
  }, [phone])

  // Reset green check when switching prospect
  useEffect(() => {
    setAnalyzeSuccess(false)
  }, [phone])

  // Gatillar análisis
  const handleAnalyze = async () => {
    setAnalyzing(true)
    setError(null)
    try {
      const res = await supervisorAPI.triggerAnalysis(phone)
      if (res?.analysis) {
        setAnalysis(res.analysis)
        setAnalyzeSuccess(true)
      } else if (res?.error) {
        setError(res.message || res.error)
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data?.message || 'Análisis reciente, espera 5 minutos')
        if (err.response.data?.analysis) {
          setAnalysis(err.response.data.analysis)
        }
      } else if (err.response?.status === 503) {
        setError(err.response.data?.message || 'Análisis GPT desactivado')
      } else {
        setError('Error al ejecutar análisis')
      }
    } finally {
      setAnalyzing(false)
    }
  }

  // Formato de probabilidad visual
  const probBar = (prob) => {
    if (prob == null) return null
    const pct = Math.round(prob * 100)
    const color = pct >= 60 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : pct >= 20 ? 'bg-orange-400' : 'bg-red-400'
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-semibold text-gray-700 w-10 text-right">{pct}%</span>
      </div>
    )
  }

  // Formato de etapa
  const etapaLabel = (etapa) => {
    const map = {
      recibido: { text: 'Recibido', bg: 'bg-gray-100 text-gray-700' },
      interactuando: { text: 'Interactuando', bg: 'bg-blue-100 text-blue-700' },
      calificado: { text: 'Calificado', bg: 'bg-indigo-100 text-indigo-700' },
      derivado: { text: 'Derivado', bg: 'bg-green-100 text-green-700' },
      frio: { text: 'Frío', bg: 'bg-orange-100 text-orange-700' },
      perdido: { text: 'Perdido', bg: 'bg-red-100 text-red-700' },
      consulta_funciones: { text: 'Consulta Funciones', bg: 'bg-cyan-100 text-cyan-700' },
      consulta_precio: { text: 'Consulta Precio', bg: 'bg-teal-100 text-teal-700' },
      seguimiento: { text: 'Seguimiento', bg: 'bg-sky-100 text-sky-700' },
      sin_respuesta: { text: 'Sin Respuesta', bg: 'bg-stone-100 text-stone-600' },
      listo_comprar: { text: 'Listo para Comprar', bg: 'bg-emerald-100 text-emerald-700' },
      solo_mirando: { text: 'Solo Mirando', bg: 'bg-slate-100 text-slate-600' },
      no_interesado: { text: 'No Interesado', bg: 'bg-rose-100 text-rose-700' },
      cotizacion: { text: 'Cotización', bg: 'bg-violet-100 text-violet-700' },
    }
    const info = map[etapa] || { text: etapa || 'Sin datos', bg: 'bg-gray-100 text-gray-600' }
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${info.bg}`}>{info.text}</span>
  }

  // Formato de fecha corta
  const fmtDate = (dateStr) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleString('es-CL', {
        timeZone: 'America/Santiago',
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
      })
    } catch { return dateStr }
  }

  // Actor badge
  const actorBadge = (actor) => {
    const map = {
      cliente: 'bg-blue-100 text-blue-700',
      bot: 'bg-gray-100 text-gray-600',
      humano: 'bg-purple-100 text-purple-700',
    }
    return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${map[actor] || 'bg-gray-100 text-gray-600'}`}>{actor}</span>
  }

  if (!phone) return null

  return (
    <AccordionSection title="Análisis IA" icon="🤖" defaultOpen={false}>
      {loading ? (
        <div className="flex items-center gap-2 py-3">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Cargando análisis...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Error */}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Sin análisis */}
          {!analysis && !error && (
            <p className="text-xs text-gray-500">Este contacto no ha sido analizado por el supervisor IA.</p>
          )}

          {/* Con análisis */}
          {analysis && (
            <>
              {/* Header: etapa + fecha */}
              <div className="flex items-center justify-between">
                {etapaLabel(analysis.intencion_cliente)}
                <span className="text-xs text-gray-400">
                  {fmtDate(analysis.analizado_en)} (v{analysis.version_analisis || 1})
                </span>
              </div>

              {/* Probabilidad de venta */}
              {analysis.probabilidad_venta != null && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Prob. venta</label>
                  {probBar(analysis.probabilidad_venta)}
                </div>
              )}

              {/* Resumen */}
              {analysis.resumen_conversacion && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Resumen</label>
                  <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{analysis.resumen_conversacion}</p>
                </div>
              )}

              {/* Acción sugerida */}
              {analysis.accion_sugerida && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                  <label className="text-xs font-medium text-indigo-600 uppercase tracking-wider">Sugerencia</label>
                  <p className="text-sm text-indigo-800 mt-0.5">{analysis.accion_sugerida}</p>
                </div>
              )}

              {/* Patrón horario */}
              {analysis.patron_horario && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <label className="text-xs font-medium text-amber-600 uppercase tracking-wider">Patrón Horario</label>
                  <p className="text-sm text-amber-800 mt-0.5">{analysis.patron_horario}</p>
                </div>
              )}

              {/* Cronología */}
              {analysis.cronologia_eventos && analysis.cronologia_eventos.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Cronología</label>
                  <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                    {(typeof analysis.cronologia_eventos === 'string'
                      ? JSON.parse(analysis.cronologia_eventos)
                      : analysis.cronologia_eventos
                    ).map((evt, i) => (
                      <div key={i} className="flex items-start gap-2 text-[11px]">
                        <span className="text-gray-400 whitespace-nowrap font-mono">{evt.fecha}</span>
                        {actorBadge(evt.actor)}
                        <span className="text-gray-700">{evt.evento}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Métricas compactas */}
              <div className="flex gap-3 text-[10px] text-gray-500 pt-1 border-t border-gray-100">
                <span>{analysis.total_mensajes || 0} msgs</span>
                <span>{analysis.mensajes_cliente || 0} cliente</span>
                <span>{analysis.mensajes_agente || 0} agente</span>
                {analysis.agente_asignado && <span>Asign: {analysis.agente_asignado}</span>}
              </div>
            </>
          )}

          {/* Botón analizar */}
          {analyzeSuccess ? (
            <button disabled className="w-full py-2 px-3 rounded-lg text-sm font-medium bg-green-100 text-green-700 border border-green-300 transition-all duration-500">
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Análisis actualizado
              </span>
            </button>
          ) : (
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              className={`
                w-full py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200
                ${analyzing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-300'
                }
              `}
            >
              {analyzing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-3 h-3 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin" />
                  Analizando...
                </span>
              ) : analysis ? 'Actualizar análisis' : 'Analizar ahora'}
            </button>
          )}
        </div>
      )}
    </AccordionSection>
  )
}
